<template>
  <component :is="shell">
    <h1 v-if="!embedded">{{ mode === 'bom' ? '服务器估价' : '卡价库' }}</h1>
    <p v-if="!embedded" class="sub">{{ mode === 'bom' ? '已发布的整机 BOM 都列在这里，合计来自组件单价。' : '官方、市场和二手参考价放在一起看。' }}</p>

    <template v-if="mode === 'card'">
      <form v-if="!embedded" class="row" @submit.prevent="search">
        <input v-model="keyword" placeholder="卡名，如 H100、昇腾 910、GB300" />
        <button type="submit" :disabled="loading">{{ loading ? '查询中…' : '查价格' }}</button>
      </form>
      <p v-if="chargeTip" class="charge">{{ chargeTip }}</p>
      <p v-if="preview" class="preview-note">
        未登录仅展示 {{ cards.length }} 款的部分价格<span v-if="resultTotal > cards.length">（共 {{ resultTotal }} 款）</span>。
        <router-link to="/account?mode=login">登录</router-link>
        后查看完整报价。
      </p>
      <CreditNext v-if="gateStatus" :status="gateStatus" :message="error" />
      <p v-else-if="error" class="error">{{ error }}</p>
      <QueryProcessRail
        v-if="processSteps.length"
        :steps="processSteps"
        :active="loading"
        :title="loading ? '正在规划并查询报价…' : '本次查价过程'"
      />
      <p v-if="searched && !loading && !gateStatus && !error && !cards.length" class="muted empty">没有找到价格数据，试试更短的关键词。</p>
      <article v-for="card in cards" :key="card.id" class="card">
        <h3>{{ card.name }}</h3>
        <table v-if="flatQuotes(card).length" class="specs">
          <thead>
            <tr>
              <th>规格</th>
              <th>类型</th>
              <th>价格</th>
              <th>月份</th>
              <th>时间戳</th>
              <th>来源</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(q, idx) in flatQuotes(card)" :key="(q.price_id || idx) + '-' + q.type">
              <td>{{ q.variant_name || card.name }}</td>
              <td><span class="type" :class="q.type">{{ q.type_label }}</span></td>
              <td>{{ q.amount }} {{ q.currency }}</td>
              <td>{{ q.price_month || '—' }}</td>
              <td>{{ formatTime(q.observed_at) }}</td>
              <td>
                <a v-if="q.source_url" :href="q.source_url" target="_blank" rel="noopener">{{ q.source_label }}</a>
                <span v-else>{{ q.source_label || '—' }}</span>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted">暂无价格数据</p>
        <p v-if="card.locked && hiddenPriceCount(card) > 0" class="preview-more">
          另有 {{ hiddenPriceCount(card) }} 条价格未展示，登录后可见。
        </p>
        <div
          v-if="hasSeries(card)"
          :id="'price-chart-' + card.id"
          class="chart-wrap"
        ></div>
        <p class="discuss-row">
          <router-link
            class="discuss"
            :to="{ path: '/forum', query: { compose: '1', card_id: String(card.id), card_name: card.name, title: card.name + ' 价格' } }"
          >讨论这张卡的价格</router-link>
        </p>
      </article>
      <RelatedArticles
        :card-ids="cards.map((c) => c.id)"
        :keyword="keyword.trim()"
        :active="searched && !loading"
      />
      <RelatedDiscussions
        :card-ids="cards.map((c) => c.id)"
        :cards="cards"
        :keyword="keyword.trim()"
        :active="searched && !loading"
      />
    </template>

    <template v-else>
      <p v-if="bomError" class="error">{{ bomError }}</p>
      <p v-else-if="!bomBoards.length" class="muted">暂无已发布的 BOM。</p>
      <article v-for="board in bomBoards" :key="board.id" class="card bom-board">
        <div class="bom-head">
          <h3>{{ board.name }}</h3>
          <strong>{{ board.total }} {{ board.currency }}</strong>
        </div>
        <p v-if="board.description" class="muted">{{ board.description }}</p>
        <ul>
          <li v-for="item in board.items" :key="item.id">
            <span>{{ catLabel(item.category) }} · {{ item.name }}</span>
            <strong>{{ item.unit_price }} {{ item.currency }}</strong>
          </li>
        </ul>
      </article>
    </template>
  </component>
</template>

<script>
import { nextTick } from 'vue'
import * as echarts from 'echarts'
import AppShell from '../layouts/AppShell.vue'
import CreditNext from '../components/CreditNext.vue'
import RelatedDiscussions from '../components/RelatedDiscussions.vue'
import RelatedArticles from '../components/RelatedArticles.vue'
import QueryProcessRail from '../components/QueryProcessRail.vue'
import {
  catalogPrices,
  listBomComponents,
  listBomTemplates
} from '../api/platformService'
import { notifyAuthChanged } from '../utils/authBus'
import {
  buildPriceSearchSteps,
  createProcessRunner
} from '../utils/queryProcessScript'

function parseAmount(text) {
  if (text == null || text === '') return null
  const m = String(text).replace(/,/g, '').match(/(\d+(?:\.\d+)?)/)
  return m ? Number(m[1]) : null
}

const CAT_LABELS = {
  cpu: 'CPU',
  motherboard: '主板',
  memory: '内存',
  ssd: 'SSD',
  gpu: 'GPU'
}

export default {
  name: 'PriceWorkspace',
  components: { AppShell, RelatedDiscussions, RelatedArticles, CreditNext, QueryProcessRail },
  props: {
    embedded: { type: Boolean, default: false },
    panel: { type: String, default: '' }
  },
  data() {
    return {
      mode: 'card',
      keyword: '',
      cards: [],
      error: '',
      chargeTip: '',
      preview: false,
      resultTotal: 0,
      loading: false,
      searched: false,
      gateStatus: 0,
      chartInstances: {},
      bomComponents: [],
      templates: [],
      bomError: '',
      processSteps: [],
      processRunner: null
    }
  },
  computed: {
    shell() {
      return this.embedded ? 'div' : AppShell
    },
    bomBoards() {
      const byId = {}
      this.bomComponents.forEach((row) => {
        byId[row.id] = row
      })
      return this.templates.map((tpl) => {
        let raw = []
        try {
          raw = JSON.parse(tpl.component_ids_json || '[]')
        } catch (e) {
          raw = []
        }
        const items = raw
          .map((id) => byId[Number(id)])
          .filter(Boolean)
        const total = items.reduce((sum, item) => sum + (parseAmount(item.unit_price) || 0), 0)
        return {
          id: tpl.id,
          name: tpl.name,
          description: tpl.description || '',
          items,
          total: Math.round(total * 100) / 100,
          currency: (items[0] && items[0].currency) || 'CNY'
        }
      })
    }
  },
  watch: {
    panel: {
      immediate: true,
      handler(value) {
        if (!this.embedded || !value) return
        this.switchMode(value === 'bom' ? 'bom' : 'card')
      }
    },
    '$route.query.mode': {
      immediate: true,
      handler(value) {
        if (this.embedded) return
        this.switchMode(value === 'bom' ? 'bom' : 'card')
      }
    }
  },
  beforeUnmount() {
    if (this.processRunner) this.processRunner.stop()
    this.disposeCharts()
  },
  methods: {
    catLabel(cat) {
      return CAT_LABELS[cat] || cat
    },
    stopProcessRunner() {
      if (this.processRunner) {
        this.processRunner.stop()
        this.processRunner = null
      }
    },
    disposeCharts() {
      Object.values(this.chartInstances).forEach((c) => {
        try {
          c.dispose()
        } catch (e) {
          /* ignore */
        }
      })
      this.chartInstances = {}
    },
    async switchMode(next) {
      this.mode = next
      if (next === 'bom') {
        this.disposeCharts()
        await this.loadBom()
      }
    },
    flatQuotes(card) {
      const rows = []
      const prices = card.prices || []
      prices.forEach((p) => {
        if (p.quotes && p.quotes.length) {
          rows.push(...p.quotes)
          return
        }
        const base = {
          price_id: p.id,
          variant_name: p.variant_name,
          currency: p.currency,
          price_month: p.price_month,
          observed_at: p.observed_at,
          source_label: p.source_label,
          source_url: p.source_url
        }
        if (p.msrp) rows.push({ ...base, type: 'official', type_label: '官方', amount: p.msrp })
        if (p.market_price) rows.push({ ...base, type: 'market', type_label: '市场', amount: p.market_price })
        if (p.secondhand_price) {
          rows.push({ ...base, type: 'secondhand', type_label: '二手', amount: p.secondhand_price })
        }
      })
      return rows
    },
    hiddenPriceCount(card) {
      const shown = (card.prices || []).length
      const total = Number(card.price_count || 0)
      return Math.max(0, total - shown)
    },
    formatTime(iso) {
      if (!iso) return '—'
      const s = String(iso).replace('T', ' ')
      return s.length >= 16 ? s.slice(0, 16) : s
    },
    hasSeries(card) {
      const series = card.price_series || []
      const counts = { official: 0, market: 0, secondhand: 0 }
      series.forEach((s) => {
        const points = s.points || []
        points.forEach((p) => {
          if (p.msrp) counts.official += 1
          if (p.market_price) counts.market += 1
          if (p.secondhand_price) counts.secondhand += 1
        })
      })
      return Object.values(counts).some((n) => n >= 2)
    },
    async search() {
      const q = this.keyword.trim()
      if (!q) return
      this.error = ''
      this.chargeTip = ''
      this.preview = false
      this.resultTotal = 0
      this.gateStatus = 0
      this.loading = true
      this.searched = true
      this.cards = []
      this.disposeCharts()
      this.stopProcessRunner()
      const steps = buildPriceSearchSteps({ q })
      this.processSteps = steps
      this.processRunner = createProcessRunner(steps, {
        onUpdate: (next) => {
          this.processSteps = next
        }
      })
      this.processRunner.start()
      try {
        const data = await catalogPrices({ q })
        this.cards = data.cards || []
        this.preview = Boolean(data.preview)
        this.resultTotal = data.total != null ? data.total : this.cards.length
        if (data.preview) {
          this.chargeTip = ''
        } else if (data.charged && data.charge_delta) {
          this.chargeTip = `本次查询已扣 ${data.charge_delta} 积分`
          notifyAuthChanged()
        } else if (data.role === 'user') {
          this.chargeTip = '相同条件重复查询不重复扣费'
        } else if (data.role === 'admin') {
          this.chargeTip = '管理员查询不扣积分'
        }
        await nextTick()
        this.cards.forEach((card) => this.renderChart(card))
      } catch (err) {
        this.cards = []
        this.processSteps = []
        this.stopProcessRunner()
        const status = err.response?.status
        this.gateStatus = status === 401 || status === 402 ? status : 0
        this.error = err.response?.data?.error || '查询失败'
      } finally {
        if (this.processRunner) this.processRunner.finish()
        this.processRunner = null
        this.loading = false
      }
    },
    renderChart(card) {
      if (!this.hasSeries(card)) return
      const el = document.getElementById(`price-chart-${card.id}`)
      if (!el) return
      if (this.chartInstances[card.id]) {
        try {
          this.chartInstances[card.id].dispose()
        } catch (e) {
          /* ignore */
        }
      }
      const chart = echarts.init(el)
      this.chartInstances[card.id] = chart
      const months = new Set()
      const seriesList = card.price_series || []
      seriesList.forEach((s) => {
        const pts = s.points || []
        pts.forEach((p) => {
          if (p.month) months.add(p.month)
        })
      })
      const x = Array.from(months).sort()
      const lines = []
      seriesList.forEach((s) => {
        const byMonth = {}
        const points = s.points || []
        points.forEach((p) => {
          byMonth[p.month] = p
        })
        const official = x.map((m) => parseAmount(byMonth[m]?.msrp))
        const market = x.map((m) => parseAmount(byMonth[m]?.market_price))
        const secondhand = x.map((m) => parseAmount(byMonth[m]?.secondhand_price))
        const label = `${s.variant_name || card.name}${s.currency ? ` · ${s.currency}` : ''}`
        if (official.some((v) => v != null)) {
          lines.push({
            name: `${label} · 官方`,
            type: 'line',
            smooth: true,
            showSymbol: true,
            lineStyle: { type: 'dashed' },
            data: official
          })
        }
        if (market.some((v) => v != null)) {
          lines.push({ name: `${label} · 市场`, type: 'line', smooth: true, showSymbol: true, data: market })
        }
        if (secondhand.some((v) => v != null)) {
          lines.push({
            name: `${label} · 二手`,
            type: 'line',
            smooth: true,
            showSymbol: true,
            lineStyle: { type: 'dotted' },
            data: secondhand
          })
        }
      })
      chart.setOption({
        color: ['#165dff', '#00b42a', '#f77234', '#14c9c9', '#f7ba1e', '#f53f3f'],
        tooltip: {
          trigger: 'axis',
          formatter(params) {
            const head = params[0]?.axisValue || ''
            const linesHtml = params
              .filter((p) => p.data != null)
              .map((p) => `${p.marker}${p.seriesName}: ${p.data}`)
              .join('<br/>')
            return `${head}<br/>${linesHtml}`
          }
        },
        legend: { type: 'scroll', bottom: 0, textStyle: { fontSize: 11, color: '#4e5969' } },
        grid: { left: 48, right: 16, top: 28, bottom: 56 },
        xAxis: { type: 'category', data: x, boundaryGap: false, axisLabel: { color: '#86909c' } },
        yAxis: {
          type: 'value',
          scale: true,
          axisLabel: { color: '#86909c' },
          splitLine: { lineStyle: { color: '#e5e6eb' } }
        },
        series: lines
      })
    },
    async loadBom() {
      this.bomError = ''
      try {
        const [components, templates] = await Promise.all([listBomComponents(), listBomTemplates()])
        this.bomComponents = components || []
        this.templates = templates || []
      } catch (err) {
        this.bomError = err.response?.data?.error || '加载 BOM 失败'
      }
    },
  }
}
</script>

<style scoped>
h1 { font-size: 34px; font-weight: 600; letter-spacing: -0.03em; margin: 0 0 8px; }
.sub { color: #86909c; margin-bottom: 16px; font-size: 15px; }
.tabs { display: flex; gap: 8px; margin-bottom: 14px; }
.tabs button {
  border: 1px solid #e5e6eb; background: #fff; color: #4e5969; border-radius: 12px; padding: 7px 12px; cursor: pointer;
}
.tabs button.on { background: #e8f3ff; color: #165dff; border-color: #bedaff; font-weight: 600; }
.row { display: flex; gap: 8px; margin-bottom: 16px; }
.row input { flex: 1; border: 1px solid #e5e6eb; border-radius: 12px; padding: 8px 10px; }
.row button, button:not(.ghost):not(.tabs button) {
  border: 0; background: #165dff; color: #fff; border-radius: 12px; padding: 8px 14px; cursor: pointer;
}
.ghost { border: 1px solid #e5e6eb; background: #fff; color: #4e5969; border-radius: 12px; padding: 6px 10px; cursor: pointer; margin-right: 6px; margin-bottom: 6px; }
.card { background: #fff; border: 1px solid #e5e6eb; border-radius: 16px; padding: 16px; margin-bottom: 12px; }
.card h3 { margin: 0 0 8px; }
.muted { color: #86909c; }
.empty { padding: 20px 0; }
.charge { color: #188038; font-size: 12px; margin: -8px 0 12px; }
.preview-note, .preview-more {
  margin: 0 0 12px;
  font-size: 13px;
  color: #4e5969;
  background: #fff7e8;
  border: 1px solid #ffe4ba;
  border-radius: 12px;
  padding: 8px 12px;
}
.preview-more { margin-top: 10px; }
.preview-note a { color: #165dff; text-decoration: none; }
.tip { margin-top: 10px; font-size: 13px; }
.error { color: #d3382c; }
.error a { margin-left: 8px; color: #165dff; }
.specs { width: 100%; border-collapse: collapse; }
.specs th, .specs td { border-top: 1px solid #e5e6eb; padding: 8px 6px; text-align: left; font-size: 14px; }
.specs th { color: #4e5969; font-weight: 500; }
.specs a { color: #165dff; text-decoration: none; }
.specs a:hover { text-decoration: underline; }
.type {
  display: inline-block; min-width: 36px; text-align: center;
  font-size: 12px; padding: 1px 6px; border-radius: 999px; background: #f2f3f5; color: #4e5969;
}
.type.official { background: #e8f3ff; color: #165dff; }
.type.market { background: #e8ffea; color: #00b42a; }
.type.secondhand { background: #fff7e8; color: #ff7d00; }
.chart-wrap {
  height: 280px; margin-top: 16px; border: 1px solid #e5e6eb; border-radius: 12px; background: #fafbfc;
}
.bom-board h3 { margin: 0; font-size: 16px; }
.bom-head { display: flex; justify-content: space-between; gap: 12px; align-items: baseline; }
.bom-board ul { list-style: none; margin: 10px 0 0; padding: 0; display: grid; gap: 6px; }
.bom-board li {
  display: flex; justify-content: space-between; gap: 12px;
  background: #f7f8fa; border-radius: 12px; padding: 8px 10px; font-size: 13px;
}
.discuss-row { margin-top: 10px; }
.discuss { color: #165dff; font-size: 13px; text-decoration: none; }
.discuss:hover { text-decoration: underline; }
</style>
